import {
    Autocomplete,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    Paper,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import { mixed, object, string } from 'yup';
import React from 'react';
import { Form, Formik } from 'formik';
import InputForm from '../../commun/input-form';
import './address-contract.form.scss';
import { Link } from 'react-router-dom';
import { AddressContractFormDto } from '../../../services/dtos/form/address-contract-form.dto';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import { AddressGouvFeaturesPropertiesFormDto } from '../../../services/dtos/form/address-gouv-features-properties-form.dto';
import { parseHouseNumber } from '../../../utils/address-parser.utils';

/**
 * Composant la page relevè de compteur
 * @constructor
 */
export default function AddressContractForm(props: {
    addressContract: string | undefined;
    handleSumbitForm: (values: AddressContractFormDto) => void;
    handleChangeCountry: (e: React.SyntheticEvent, v: ReferenceDto | null) => void;
    validForm: boolean;
    id: string | undefined;
    countriesSelection: ReferenceDto[];
    selectedCountry: ReferenceDto;
    handleSearchAddress: (event: object, value: string, reason: string) => void;
    addressesGouv: AddressGouvFeaturesPropertiesFormDto[];
    handleSelectAddress: (address: AddressGouvFeaturesPropertiesFormDto | null) => void;
}) {
    // Schéma de validation des données
    const validationSchema = object({
        //Validation du champ adresse auto-complete (optionnel, aide juste au remplissage)
        address: mixed<AddressGouvFeaturesPropertiesFormDto>().notRequired(),
        //Validation du champ numero rue
        numeroRue: string().trim().required('Veuillez renseigner votre numéro de rue').matches(/^\d+$/, {
            message: 'Seuls les caractères numériques sont autorisés',
        }),
        //Validation du champ rue
        rue: string().trim().required('Veuillez renseigner votre nom de rue'),
        //Validation du champ ville
        ville: string().trim().required('Veuillez renseigner votre ville de résidence'),
        //Validation du champ pays
        pays: mixed<ReferenceDto>().required('Veuillez choisir un pays'),
        //Validation du champ code postal
        codePostal: string().trim().required('Veuillez renseigner votre code postal').matches(/^\d+$/, {
            message: 'Seul les caractères numériques sont autorisés',
        }),
        //Validation du champ boite postal
        boitePostale: string().trim().notRequired(),
        //Validation des champs sur complement
        complement: object().shape({
            batiment: string().trim().notRequired(),
            etage: string().trim().notRequired(),
            appartement: string().trim().notRequired(),
            escalier: string().trim().notRequired(),
            ville: string().trim().notRequired(),
            numeroRue: string().trim().notRequired(),
            rue: string().trim().notRequired(),
        }),
    });

    // Valeurs par défaut
    const iniValues = {
        address: '',
        numeroRue: '',
        rue: '',
        ville: '',
        codePostal: '',
        boitePostale: '',
        pays: { value: '', label: '' },
        complement: {
            batiment: '',
            etage: '',
            appartement: '',
            escalier: '',
            ville: '',
            numeroRue: '',
            rue: '',
        },
    };

    // Création de la page
    return (
        // Mise en place d'une card
        <Card className="address-contract-form">
            <CardHeader title={<Typography variant="h5">Nouvelle adresse de correspondance</Typography>}></CardHeader>
            <CardContent>
                {/*Phrase explicative pour les abonnés*/}
                <Container sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Paper elevation={2} className="imgText" sx={{ padding: '20px' }}>
                        {/*adresse actuelle*/}
                        <Typography variant="body1">Adresse actuelle : </Typography>
                        {props.addressContract ? (
                            <Typography variant="subtitle1">{props.addressContract}</Typography>
                        ) : (
                            <Skeleton variant="rectangular" height={40}></Skeleton>
                        )}
                    </Paper>
                </Container>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5">Votre nouvelle adresse de correspondance</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                    </Grid>
                </Grid>
                {/*Formulaire avec tableau pour afficher correctement les champs*/}
                <Formik initialValues={iniValues} onSubmit={props.handleSumbitForm} validationSchema={validationSchema}>
                    {({ setFieldValue }) => (
                        <Form>
                            {/*Autocomplete pour recherche d'adresse via API data.gouv*/}
                            <Autocomplete
                                data-testid="autocomplete-address"
                                disablePortal
                                id="address"
                                autoHighlight
                                options={props.addressesGouv}
                                getOptionLabel={option => option.label}
                                onInputChange={props.handleSearchAddress}
                                onChange={(_, value) => {
                                    if (value) {
                                        // Parser le numéro de rue pour séparer le numéro des bis/ter
                                        const parsedNumber = parseHouseNumber(value.housenumber || '');

                                        // Remplir automatiquement les champs du formulaire
                                        setFieldValue('numeroRue', parsedNumber.numero);
                                        setFieldValue('complement.numeroRue', parsedNumber.complement);
                                        setFieldValue('rue', value.street || value.name || '');
                                        setFieldValue('ville', value.city || '');
                                        setFieldValue('codePostal', value.postcode || '');
                                        props.handleSelectAddress(value);
                                    } else {
                                        props.handleSelectAddress(null);
                                    }
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Rechercher votre adresse (optionnel)"
                                        helperText="Tapez votre adresse pour remplir automatiquement les champs ci-dessous"
                                        sx={{ marginTop: '20px', marginBottom: '20px' }}
                                    />
                                )}
                            />

                            {/*Début grid - Formulaire principal toujours visible*/}
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={2}>
                                    {/*Champ numero de rue*/}
                                    <InputForm id="numeroRue" name="numeroRue" required label={'N°'}></InputForm>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    {/*Champ complement numero de rue*/}
                                    <InputForm
                                        id="complementnumeroRue"
                                        name="complement.numeroRue"
                                        label={'Bis, Ter, ...'}
                                    ></InputForm>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    {/*Champ nom de rue*/}
                                    <InputForm id="rue" name="rue" required label={'Rue'}></InputForm>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    {/*Champ complément de rue*/}
                                    <InputForm
                                        id="complementRue"
                                        name="complement.rue"
                                        label={'Complément de rue'}
                                    ></InputForm>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {/*Champ entree ou residence...*/}
                                    <InputForm
                                        id="batiment"
                                        name="complement.batiment"
                                        label={'Entrée, résidence...'}
                                    ></InputForm>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {/*Champ étage*/}
                                    <InputForm id="etage" name="complement.etage" label={'Etage'}></InputForm>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {/*Champ appartement*/}
                                    <InputForm
                                        id="appartement"
                                        name="complement.appartement"
                                        label={'Appartement'}
                                    ></InputForm>
                                </Grid>
                                {/*Champ complement d'adresse*/}
                                <Grid item xs={12} sm={6}>
                                    <InputForm id="escalier" name="complement.escalier" label={'Escalier'}></InputForm>
                                </Grid>
                                {/*Champ code postal*/}
                                <Grid item xs={12} sm={4}>
                                    <InputForm
                                        id="codePostal"
                                        name="codePostal"
                                        required
                                        label={'Code postal'}
                                    ></InputForm>
                                </Grid>
                                {/*Champ ville*/}
                                <Grid item xs={12} sm={4}>
                                    <InputForm id="ville" name="ville" required label={'Ville'}></InputForm>
                                </Grid>
                                {/*Champ complement ville*/}
                                <Grid item xs={12} sm={4}>
                                    <InputForm
                                        id="complementville"
                                        name="complement.ville"
                                        label={'Complément ville'}
                                    ></InputForm>
                                </Grid>
                                {/*Champ boite postale*/}
                                <Grid item xs={12} sm={4}>
                                    <InputForm
                                        id="boitePostale"
                                        name="boitePostale"
                                        label={'Boite postale'}
                                    ></InputForm>
                                </Grid>
                                {/*Champ pays*/}
                                {props.countriesSelection ? (
                                    <Grid item xs={12} sm={4}>
                                        <Autocomplete
                                            data-testid="autocomplete-pays"
                                            disablePortal
                                            id="pays"
                                            autoHighlight
                                            options={props.countriesSelection}
                                            getOptionLabel={option => option.value}
                                            value={props.selectedCountry}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            onChange={(e, value) => {
                                                props.handleChangeCountry(e, value);
                                            }}
                                            renderInput={params => (
                                                <TextField {...params} name="pays" label="Pays" required />
                                            )}
                                        ></Autocomplete>
                                    </Grid>
                                ) : (
                                    <Skeleton variant="rectangular" height={100}></Skeleton>
                                )}
                                {/*Actions du formulaire*/}
                                <Grid item xs={0} sm={9}></Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant={'button'} className={'buttons-form'}>
                                        {/*bouton annulé*/}
                                        <Button variant="neutral" component={Link} to={'/contrat/' + props.id}>
                                            Annuler
                                        </Button>
                                        {/*bouton valider*/}
                                        <Button type="submit" variant="contained" disabled={!props.validForm}>
                                            Valider
                                        </Button>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </CardContent>
        </Card>
    );
}
